import React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Icon from 'src/components/Icon';

// Save status indicator.
//   LOADING    — loading=true → grey "Loading…" pill, ahead of every other state.
//   NOT WIRED  — hasOnSave=false → renders nothing.
//   DIRTY      — isDirty=true → amber "Unsaved changes" pill.
//   SAVED      — within 2 s of lastSavedAt while clean → green "Saved" flash.
export default function SaveStatusPill({
  isDirty,
  lastSavedAt,
  hasOnSave,
  loading,
}: {
  isDirty: boolean;
  lastSavedAt: number | null | undefined;
  hasOnSave: boolean;
  loading?: boolean;
}) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!lastSavedAt) return undefined;
    setNow(Date.now());
    const id = setTimeout(() => setNow(Date.now()), 2100);
    return () => clearTimeout(id);
  }, [lastSavedAt]);

  if (loading) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] font-medium rounded-md px-2 py-0.5 bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border border-zinc-500/30"
        aria-live="polite"
        data-testid="save-status-loading"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" aria-hidden />
        <Trans>Loading…</Trans>
      </span>
    );
  }

  if (!hasOnSave) return null;
  const justSaved = !isDirty && lastSavedAt && now - lastSavedAt < 2000;

  if (isDirty) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] font-medium rounded-md px-2 py-0.5 bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30"
        title={t`You have unsaved changes — press ⌘S to save`}
        aria-live="polite"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" aria-hidden />
        <Trans>Unsaved changes</Trans>
      </span>
    );
  }

  if (justSaved) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] font-medium rounded-md px-2 py-0.5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 transition-opacity"
        aria-live="polite"
      >
        <Icon icon="Check" className="text-[9px]" />
        <Trans>Saved</Trans>
      </span>
    );
  }

  return null;
}
